import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Stack,
} from "@mui/material";
import { AccessContext } from "App/components/Access-Control/AccessProvider";

import DataGrid from "global/components/UI/DataGrid/DataGrid";
import DatePickerElement from "global/components/UI/DatePicker/DatePicker";
import TextValue from "global/components/UI/TextValue/TextValue";
import UIButton from "global/components/UI/UIButton/UIButton";
import useModalController from "global/hook/modal/use-modals";
import DateUtils, { getTomorrow } from "global/util/DateUtils";

import { useContext, useState } from "react";
import AutomatenblattInformation from "service/data-service/automate-controller/interface/AutomatenblattInformation";
import AutomatenUpdateRequest from "service/data-service/automate-controller/interface/AutomatenUpdateRequest";
import RnsSearchResult from "service/data-service/rns-controller/interface/RnsSearchResult";
import { generateDefaultRnsSearchResult } from "./generator/defaultObjectGenerators";
import "./glnChange.scss";
import {
  MachineIdentifier,
  OtherMachineToSave,
} from "./interface/otherMachinesToSave";
import createGlnChangeColumnDefs from "./script/columnDefinition";
import GlnChangeController from "./script/GlnChangeController";

interface GlnChangeProps {
  automatenblattInformation: AutomatenblattInformation;
  updateValues: React.MutableRefObject<AutomatenUpdateRequest>;
  onGlnChange: () => void;
}

const GlnChange: React.FC<GlnChangeProps> = (props: GlnChangeProps) => {
  const [open, setOpen] = useState(false);
  const [glnToSearch, setGlnToSearch] = useState("");
  const [searchResult, setSearchResult] = useState<RnsSearchResult>(
    generateDefaultRnsSearchResult()
  );
  const [gueltigVonDefaultTomorrow] = useState(getTomorrow());
  const [otherMachinesToSave, setOtherMachinesToSave] = useState<
    Array<OtherMachineToSave>
  >([]);
  const accessContext = useContext(AccessContext);

  function addMachineToSaveableMachineArray(machineToSave: MachineIdentifier) {
    setOtherMachinesToSave((state) => {
      return [...state, { ...machineToSave, saveAlso: false }];
    });
  }

  function setMachineWillBeSaved(serialNumber: string, newValue: boolean) {
    setOtherMachinesToSave((state) => {
      let indexOfMachineToSave = state.findIndex((value) => {
        return serialNumber === value.serialNumber;
      });

      state[indexOfMachineToSave].saveAlso = newValue;
      return state;
    });
  }

  const [selectedDate, setSelectedDate] = useState<Date>(getTomorrow());
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);

  const modalController = useModalController();

  function collectOtherMachinesToSaveData() {
    if (props.automatenblattInformation.aktiveSerienNrInRns !== "") {
      GlnChangeController.searchSerialNumbersForOtherMachinesOfRns(
        props.automatenblattInformation.aktiveSerienNrInRns,
        props.automatenblattInformation.seriennummer.toString(),
        addMachineToSaveableMachineArray
      );
    }
  }

  const openDialog = () => {
    collectOtherMachinesToSaveData();
    setOpen(true);
  };

  function closeDialog() {
    setGlnToSearch("");
    clearSearchResult();
    setOtherMachinesToSave([]);
    setOpen(false);
  }

  function handleSave() {
    GlnChangeController.updateMachineWithNewGln(
      GlnChangeController.filterMachinesToSave(otherMachinesToSave),
      props.updateValues,
      props.onGlnChange
    );
    closeDialog();
  }

  function clearSearchResult() {
    if (!saveButtonDisabled) {
      setSaveButtonDisabled(true);
      setSearchResult(generateDefaultRnsSearchResult());
    }
  }

  function prepareValuesForRnsChangeSave() {
    props.updateValues.current.datumVonForRnsWechsel =
      DateUtils.formatDateToAPIDateString(selectedDate) ?? undefined;
  }

  function onSeachConcluded(value: RnsSearchResult) {
    prepareValuesForRnsChangeSave();
    GlnChangeController.showDialogWhenOtherGh(value, modalController);
    setSearchResult(value);
    setSaveButtonDisabled(false);
  }

  return (
    <Stack flexDirection="row" columnGap={2}>
      <Button
        className="glnChangeButton"
        variant="contained"
        size="small"
        onClick={openDialog}
        disabled={
          !accessContext.getAccessContainer().showAutomatenTabBtnGlnWechsel
        }
      >
        GLN RNS Ändern
      </Button>

      <Dialog open={open} onClose={closeDialog}>
        <DialogTitle>RNS ändern</DialogTitle>
        <DialogContent className="glnSearchHeight">
          <TextValue
            onChange={(event) => {
              let newValue = event.target.value;
              setGlnToSearch(newValue);
              props.updateValues.current.rnsGlnForRnsWechsel = newValue;
              clearSearchResult();
            }}
            label="GLN der neuen RNS:"
            value={glnToSearch}
            id="gln-der-rns"
          />
        </DialogContent>
        <DialogActions className="searchAction">
          <UIButton onClick={closeDialog} label="Abbrechen" />
          <UIButton
            onClick={() =>
              GlnChangeController.searchRns(
                glnToSearch,
                props.automatenblattInformation.rns.gln.toString(),
                onSeachConcluded,
                modalController
              )
            }
            label="RNS suchen"
          />
        </DialogActions>
        <DialogContent>
          <DialogContentText>Neue Rücknahmestelle (RNS)</DialogContentText>
          <TextValue
            label="Name:"
            value={searchResult.rows[0].name}
            id="rns-name"
            readOnly
          />
          <TextValue
            label="Strasse:"
            value={searchResult.rows[0].strasse}
            id="rns-strasse"
            readOnly
          />
          <TextValue
            label="PLZ:"
            value={searchResult.rows[0].plz}
            id="rns-plz"
            readOnly
          />
          <TextValue
            label="Ort:"
            value={searchResult.rows[0].ort}
            id="rns-ort"
            readOnly
          />
          <DialogContentText marginTop={1}>
            Neuer Rücknehmer (RN)
          </DialogContentText>
          <TextValue
            label="GLN:"
            value={searchResult.rows[0].edekaOrganisation.gln}
            id="rns-gln"
            readOnly
          />
          <TextValue
            label="Name:"
            value={searchResult.rows[0].edekaOrganisation.name}
            id="rns-name"
            readOnly
          />
          <DatePickerElement
            label="Gültig von:"
            defaultValue={gueltigVonDefaultTomorrow}
            getSelectedValue={(value) => {
              setSelectedDate(new Date(value ?? getTomorrow()));
            }}
            id="rns-gultig-von"
          />
          <DialogContentText marginTop={1}>
            Weitere Automaten des alten Rücknehmers
          </DialogContentText>
          <Grid item gridColumn="span 2" xs={12}>
            <DataGrid
              height={138}
              columnDefs={createGlnChangeColumnDefs(setMachineWillBeSaved)}
              rowsDataDef={{ data: otherMachinesToSave }}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <UIButton
            onClick={handleSave}
            label="RNS speichern"
            disabled={saveButtonDisabled}
          />
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
export default GlnChange;
